"use client";

import { useQuery } from "@tanstack/react-query";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useLayoutEffect, useMemo } from "react";

import { Skeleton } from "@/components/ds/skeleton";
import { HeaderDeviceSelect } from "@/components/layout/header-device-select";
import {
  PageHeader,
  WorkspacePage,
  pageHeaderStripClasses,
  pageHeaderStripInnerClasses,
} from "@/components/ds/workspace-page";
import { cn } from "@/lib/utils/cn";
import { Card } from "@/components/ui-system";
import { getDevices } from "@/lib/api/endpoints/devices";
import { DeviceDetailView } from "@/modules/devices/components/device-detail-view";
import { DeviceDetailShell } from "@/modules/devices/components/device-detail-shell";
import { useDevice } from "@/modules/devices/hooks";
import { useAuthStore } from "@/modules/auth/store";
import { useWorkspaceChromeStore } from "@/store/workspace-chrome";

export function DeviceDetailPage(explicit: {
  deviceId: string;
  mode?: "view" | "edit";
  /** From RSC `searchParams.tab` — avoids `useSearchParams()` SSR pitfalls on device shell. */
  initialTab?: string;
}) {
  const { deviceId, mode = "view", initialTab } = explicit;
  const location = useLocation();
  const navigate = useNavigate();
  const tenantId = useAuthStore((state) => state.tenantId);
  const user = useAuthStore((state) => state.user);
  const setWorkspaceChrome = useWorkspaceChromeStore((state) => state.setWorkspaceChrome);
  const allWorkspaces = user?.role === "super_admin" || user?.accountType === "platform";
  const query = useDevice(deviceId);
  const devicesQuery = useQuery({
    queryKey: ["device-detail", "selector", allWorkspaces ? "all" : tenantId],
    queryFn: () => getDevices(allWorkspaces ? null : tenantId ?? null, allWorkspaces ? "all" : null, "devices"),
    enabled: allWorkspaces || Boolean(tenantId),
    staleTime: 30_000,
  });
  const deviceOptions = useMemo(() => {
    const devices = devicesQuery.data ?? [];
    if (query.data && !devices.some((device) => device.id === query.data?.id)) {
      return [query.data, ...devices];
    }
    return devices;
  }, [devicesQuery.data, query.data]);

  useLayoutEffect(() => {
    setWorkspaceChrome({
      headerAction: (
        <HeaderDeviceSelect
          devices={deviceOptions}
          value={deviceId}
          loading={devicesQuery.isLoading}
          disabled={!allWorkspaces && !tenantId}
          onChange={(nextDeviceId) => {
            if (nextDeviceId === deviceId) {
              return;
            }
            navigate(
              mode === "edit"
                ? `/devices/${nextDeviceId}/edit${location.search}`
                : `/devices/${nextDeviceId}${location.search}`,
            );
          }}
        />
      ),
    });
    return () => setWorkspaceChrome({ headerAction: null });
  }, [allWorkspaces, deviceId, deviceOptions, devicesQuery.isLoading, location.search, mode, navigate, setWorkspaceChrome, tenantId]);

  if (query.isLoading) {
    return (
      <WorkspacePage className="max-w-[110rem] pt-0">
        <div className="space-y-ds-8">
          <div className={cn(pageHeaderStripClasses, "mb-ds-6")}>
            <div className={pageHeaderStripInnerClasses}>
              <div className="flex flex-col gap-ds-4 sm:flex-row sm:items-start sm:justify-between sm:gap-x-ds-6">
                <div className="min-w-0 flex-1 space-y-ds-2">
                  <Skeleton className="h-7 w-48 max-w-full rounded-full" />
                  <div className="flex flex-wrap gap-ds-2">
                    <Skeleton className="h-5 w-32 rounded-full" />
                    <Skeleton className="h-5 w-24 rounded-full" />
                  </div>
                </div>
                <div className="flex shrink-0 flex-wrap justify-end gap-2 sm:justify-end">
                  <Skeleton className="h-9 w-24 rounded-full" />
                  <Skeleton className="h-9 w-28 rounded-full" />
                </div>
              </div>
            </div>
          </div>
          <div className="grid gap-ds-4 sm:grid-cols-2 lg:grid-cols-3">
            <Skeleton className="h-32 rounded-[var(--radius-lg)]" />
            <Skeleton className="h-32 rounded-[var(--radius-lg)]" />
            <Skeleton className="h-32 rounded-[var(--radius-lg)] sm:col-span-2 lg:col-span-1" />
          </div>
          <Skeleton className="h-72 rounded-[var(--radius-xl)]" />
        </div>
      </WorkspacePage>
    );
  }

  if (query.isError || !query.data) {
    return (
      <WorkspacePage className="pt-0">
        <PageHeader
          kicker="Catalog"
          title="Data logger not found"
          description="This identifier is missing from the workspace catalog, or the API could not return a record for your workspace."
          actions={
            <Link to="/devices" className="btn btn-secondary btn-sm !no-underline">
              Back to directory
            </Link>
          }
        />
        <Card className="mt-ds-6 border-error/30 bg-error/5 p-ds-6 shadow-none" role="alert">
          <p className="text-caption text-error">
            Unable to load device <span className="text-error/90">{deviceId}</span>. Check connectivity,
            credentials, and tenant scope.
          </p>
        </Card>
      </WorkspacePage>
    );
  }

  return (
    <WorkspacePage className="max-w-[110rem] pt-0">
      {mode === "edit" ? (
        <DeviceDetailShell device={query.data} initialTab={initialTab} />
      ) : (
        <DeviceDetailView device={query.data} />
      )}
    </WorkspacePage>
  );
}
